import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin } from '../lib/admin';

interface Props {
  children: React.ReactNode;
}

export function AdminGuard({ children }: Props) {
  const { user, loading } = useAuth();
  const [adminStatus, setAdminStatus] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (user && !loading) {
      checkAdminStatus();
    } else if (!loading && !user) {
      setChecking(false);
    }
  }, [user, loading]);

  const checkAdminStatus = async () => {
    try {
      const isAdminUser = await isAdmin();
      setAdminStatus(isAdminUser);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setAdminStatus(false);
    } finally {
      setChecking(false);
    }
  };

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminStatus === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-300 mb-6">You don't have permission to access the admin dashboard.</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}